import * as React from 'react';
import { jsx, Fragment } from 'react/jsx-runtime';
import { createLogger } from '@authe/fe-commons/logging';

// src/session/SessionProvider.tsx
var SessionReactContext = React.createContext(null);
function SessionProvider({ manager, children }) {
  const [state, setState] = React.useState({ status: "unknown", session: null });
  React.useEffect(() => {
    let active = true;
    manager.bootstrap().then((next) => {
      if (active) setState(next);
    }).catch((error) => {
      if (active) setState({ status: "error", session: null, error });
    });
    return () => {
      active = false;
    };
  }, [manager]);
  const refresh = React.useCallback(async () => {
    const session = await manager.loadSession();
    setState(session ? { status: "authenticated", session } : { status: "unauthenticated", session: null });
  }, [manager]);
  const value = React.useMemo(
    () => ({
      state,
      manager,
      session: state.session,
      isAuthenticated: state.status === "authenticated",
      isLoading: state.status === "unknown",
      login: (options) => manager.login(options),
      logout: (options) => manager.logout(options),
      getAccessToken: () => manager.getAccessToken(),
      refresh
    }),
    [state, manager, refresh]
  );
  return /* @__PURE__ */ jsx(SessionReactContext.Provider, { value, children });
}
function useSession() {
  const ctx = React.useContext(SessionReactContext);
  if (!ctx) throw new Error("useSession must be used inside <SessionProvider>");
  return ctx;
}
function useOptionalSession() {
  return React.useContext(SessionReactContext);
}
var log = createLogger("ui-core:session");
function useRequireSessionLogin(manager, enabled, options = {}) {
  const { returnTo, params, onAuthenticated } = options;
  const [state, setState] = React.useState({ phase: "idle", error: null });
  const paramsKey = params ? JSON.stringify(params) : "";
  const onAuthenticatedRef = React.useRef(onAuthenticated);
  onAuthenticatedRef.current = onAuthenticated;
  const paramsRef = React.useRef(params);
  paramsRef.current = params;
  React.useEffect(() => {
    if (!enabled) {
      setState({ phase: "idle", error: null });
      return void 0;
    }
    let active = true;
    setState({ phase: "pending", error: null });
    void manager.requireSession({ returnTo, params: paramsRef.current }).then((result) => {
      if (!active) return;
      if (result.outcome === "blocked") {
        setState({ phase: "blocked", error: result.error.message });
      } else if (result.outcome === "authenticated") {
        onAuthenticatedRef.current?.();
      }
    }).catch((err) => {
      if (!active) return;
      log.error("requireSession failed", { error: String(err) });
      setState({ phase: "blocked", error: err instanceof Error ? err.message : String(err) });
    });
    return () => {
      active = false;
    };
  }, [manager, enabled, returnTo, paramsKey]);
  return state;
}
function RequireSession({
  children,
  loading = null,
  blocked = null,
  error,
  returnTo,
  loginParams
}) {
  const { state, manager } = useSession();
  const login = useRequireSessionLogin(manager, state.status === "unauthenticated", {
    returnTo,
    params: loginParams
  });
  if (state.status === "authenticated") return /* @__PURE__ */ jsx(Fragment, { children });
  if (login.phase === "blocked" || state.status === "error") return /* @__PURE__ */ jsx(Fragment, { children: error ?? blocked });
  return /* @__PURE__ */ jsx(Fragment, { children: loading });
}

export { RequireSession, SessionProvider, useOptionalSession, useRequireSessionLogin, useSession };
//# sourceMappingURL=chunk-RNYC4BWW.js.map
//# sourceMappingURL=chunk-RNYC4BWW.js.map